import type { Codebook } from '@/types/api'

/**
 * Názvy stĺpcov auditu → prekladové kľúče a formátovanie hodnôt.
 *
 * Server posiela aj vlastný `label`, ale ten je natvrdo po slovensky
 * (`FIELD_LABELS` v `apps/changelog/services.py`). Preklad patrí sem, kde už
 * i18n je — inak by na nemeckej škole svietilo „pocet osob“.
 */
const LABELS: Record<string, string> = {
  // lekcia klienta
  client_id: 'reservation.client',
  instructor_reservation_id: 'reservation.instructor',
  instructor_id: 'reservation.instructor',
  start: 'reservation.start',
  end: 'reservation.end',
  status_id: 'reservation.status',
  statusid: 'reservation.status',
  lang_id: 'reservation.language',
  course_id: 'reservation.course',
  numbers: 'reservation.persons',
  client_paid: 'reservation.paid',
  // Legacy stĺpec, ktorý aplikácia nenastavuje - v prevzatých dátach sa
  // ale zmeniť mohol, tak nech má aspoň meno.
  client_payment: 'changelog.legacyPayment',
  note: 'global.note',
  is_school_course: 'reservation.schoolCourse',

  // klient a inštruktor
  fname: 'reservation.firstname',
  sname: 'reservation.surname',
  email: 'reservation.email',
  tel: 'reservation.phone',
  levelid: 'clients.level',
  is_children: 'global.child',
  is_beginner: 'global.beginner',
  deleted: 'instructors.deleted',
  idslang: 'instructors.languages',
  idscourses: 'instructors.courses',

  // blok inštruktora
  owncar: 'calendar.ownCar',
  passengers_in_car: 'calendar.passengers',
}

export function fieldLabelKey(field: string) {
  return LABELS[field] ?? field
}

/** Polia, ktorých hodnota je zoznam id oddelený čiarkou (`"1,2,3"`). */
const ID_LISTS: Record<string, 'languages' | 'courses'> = {
  idslang: 'languages',
  idscourses: 'courses',
}

/** Polia, ktoré nesú spôsob úhrady - má vlastný preklad, nie číselník. */
const PAYMENT_FIELDS = new Set(['client_paid'])

export interface ValueContext {
  t: (key: string, options?: Record<string, unknown>) => string
  /** Číselníky z bootstrapu - na rozpad zoznamov id na kľúče. */
  codebooks?: { languages?: Codebook[]; courses?: Codebook[] }
  formatDateTime: (value: string) => string
}

/**
 * Hodnota tak, aby jej rozumel človek.
 *
 * Väčšinu id preloží už server (`feeds._translate`). Sem zostáva to, čo
 * server preložiť nemôže, lebo to nie je v databáze: spôsob úhrady, `áno/nie`
 * a zoznamy id v jednom stĺpci.
 */
export function formatValue(field: string, raw: unknown, ctx: ValueContext): string {
  if (raw === null || raw === undefined || raw === '') return '—'

  if (typeof raw === 'boolean') return ctx.t(raw ? 'global.yes' : 'global.no')

  if (PAYMENT_FIELDS.has(field)) return ctx.t(`reservation.payment.${raw}`)

  const book = ID_LISTS[field]
  if (book) {
    const rows = ctx.codebooks?.[book] ?? []
    const names = String(raw)
      .split(',')
      .map((part) => Number.parseInt(part, 10))
      .filter((id) => !Number.isNaN(id))
      .map((id) => rows.find((row) => row.id === id))
      .map((row) => row?.name || row?.key)
      .filter(Boolean)
    return names.length ? names.join(', ') : '—'
  }

  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    return ctx.formatDateTime(raw)
  }

  return String(raw)
}
